import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import userMetadataApi from '../../api/userMetadataApi';
import vendorApi from '../../api/vendorApi';
import { CheckCircle, XCircle, Search, Loader2, RefreshCcw, Download, RefreshCw, KeyRound, Copy, CreditCard } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { adminDataApi } from '../../api/adminDataApi';
import DataExportModal from './components/DataExportModal';
import RenormalizeModal, { RenormalizeParams } from './components/RenormalizeModal';
import { useAdminContext } from './AdminContext';
import AdminBillingPlansModal from './components/AdminBillingPlansModal';


const AllBusinessesPage: React.FC = () => {
    const { selectedBusinessId, setSelectedBusinessId } = useAdminContext();
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const [exportModalOpen, setExportModalOpen] = useState(false);
    const [selectedBusinessForExport, setSelectedBusinessForExport] = useState<{ id: string, name: string } | null>(null);
    const [renormalizeModalOpen, setRenormalizeModalOpen] = useState(false);
    const [selectedBusinessForRenormalize, setSelectedBusinessForRenormalize] = useState<{ id: string | null, name: string | null } | null>(null);
    const [selectedBusinessForQboSync, setSelectedBusinessForQboSync] = useState<string | null>(null);
    const [generatedConnectorToken, setGeneratedConnectorToken] = useState<{ businessId: string; businessName: string; token: string } | null>(null);
    const [selectedBusinessForBilling, setSelectedBusinessForBilling] = useState<any | null>(null);

    const { data, isLoading, error } = useQuery({
        queryKey: ['allBusinesses'],
        queryFn: userMetadataApi.getAllBusinesses,
    });

    const renormalizeMutation = useMutation({
        mutationFn: ({ businessId, params }: { businessId: string | null, params: RenormalizeParams }) =>
            vendorApi.renormalizeVendors({
                ...params,
                businessId: businessId || undefined
            }),
        onSuccess: () => {
            alert('Renormalization triggered successfully');
            setRenormalizeModalOpen(false);
        },
        onError: () => {
            alert('Failed to trigger renormalization');
        }
    });

    const qboSyncMutation = useMutation({
        mutationFn: (businessId: string) =>
            adminDataApi.syncQbo({
                business_id: businessId,
                type: 'all',
            }),
        onMutate: (businessId: string) => {
            setSelectedBusinessForQboSync(businessId);
        },
        onSuccess: (result) => {
            alert(result.message || 'QBO sync triggered successfully');
            setSelectedBusinessForQboSync(null);
        },
        onError: () => {
            alert('Failed to trigger QBO sync');
            setSelectedBusinessForQboSync(null);
        }
    });

    const qbDesktopTokenMutation = useMutation({
        mutationFn: (business: any) => adminDataApi.createQbDesktopConnectorToken(getBusinessId(business)),
        onSuccess: (result, business) => {
            setGeneratedConnectorToken({
                businessId: result.business_id,
                businessName: business.legal_name || business.dba_name || business.onboarding.qbo_metadata.company_name || business.user_email || result.business_id,
                token: result.token,
            });
        },
        onError: (error: any) => {
            const detail = error?.response?.data?.detail;
            alert(typeof detail === 'string' ? detail : 'Failed to generate connector token');
        }
    });

    const handleOpenRenormalize = (business: any | null) => {
        setSelectedBusinessForRenormalize(business ? {
            id: business.business_id || business.user_id,
            name: business.legal_name || business.dba_name || business.onboarding.qbo_metadata.company_name || business.user_email
        } : {
            id: null,
            name: null
        });
        setRenormalizeModalOpen(true);
    };

    const filteredBusinesses = data?.businesses.filter(business => {
        const searchLower = searchTerm.toLowerCase();
        return (
            business.user_email?.toLowerCase().includes(searchLower) ||
            business.first_name?.toLowerCase().includes(searchLower) ||
            business.last_name?.toLowerCase().includes(searchLower) ||
            business.onboarding.qbo_metadata.company_name?.toLowerCase().includes(searchLower) ||
            business.legal_name?.toLowerCase().includes(searchLower) ||
            business.dba_name?.toLowerCase().includes(searchLower)
        );
    }) || [];

    const handleOpenExport = (business: any) => {
        setSelectedBusinessForExport({
            id: business.business_id || business.user_id,
            name: business.legal_name || business.dba_name || business.onboarding.qbo_metadata.company_name || business.user_email
        });
        setExportModalOpen(true);
    };

    const getBusinessId = (business: any) => business.business_id || business.user_id || '';
    const formatConnectedAt = (value?: string | null) => {
        if (!value) return null;
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return null;
        return date.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };
    const getQboSourceLabel = (business: any) => {
        const meta = business?.onboarding?.qbo_metadata || {};
        if (
            meta.source === 'qbdesktop' ||
            meta.provider === 'quickbooks_desktop' ||
            String(meta.realm_id || '').startsWith('qbdesktop:')
        ) {
            return 'QuickBooks Desktop';
        }
        return 'QuickBooks Online';
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-full">
                <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex justify-center items-center h-full text-red-500">
                Failed to load businesses.
            </div>
        );
    }

    return (
        <div className="p-6 h-full flex flex-col">
            <div className="mb-5 flex justify-between items-center">
                <div>
                    <h2 className="text-xl font-bold text-gray-900">All Businesses</h2>
                    <p className="text-sm text-gray-500 mt-0.5">
                        {filteredBusinesses.length} business{filteredBusinesses.length !== 1 ? 'es' : ''}
                    </p>
                </div>

                <div className="flex items-center gap-3">

                    <div className="relative w-72">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Search className="h-4 w-4 text-gray-400" />
                        </div>
                        <input
                            type="text"
                            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent sm:text-sm shadow-sm"
                            placeholder="Search businesses..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>
            </div>

            <div className="flex-1 bg-white rounded-lg shadow overflow-hidden flex flex-col">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Business
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Owner
                                </th>
                                <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Stripe Linked
                                </th>
                                <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    QBO Linked
                                </th>
                                <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Role
                                </th>
                                <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {filteredBusinesses.map((business) => {
                                const businessId = getBusinessId(business);
                                const isSelected = businessId !== '' && businessId === selectedBusinessId;

                                return (
                                <tr
                                    key={businessId}
                                    onClick={() => {
                                        setSelectedBusinessId(businessId);
                                        navigate(`/admin/business/${businessId}`);
                                    }}
                                    className={`cursor-pointer transition-colors ${isSelected ? 'bg-indigo-50 hover:bg-indigo-50' : 'hover:bg-gray-50'}`}
                                    aria-selected={isSelected}
                                >
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center">
                                            <div>
                                                <div className={`text-sm font-medium ${isSelected ? 'text-indigo-700' : 'text-gray-900'}`}>
                                                    {business.legal_name || business.dba_name || business.onboarding.qbo_metadata.company_name || 'Unknown business'}
                                                </div>
                                                <div className="text-sm text-gray-500">
                                                    {business.dba_name || business.onboarding.qbo_metadata.company_name || business.user_email}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div>
                                            <div className="text-sm font-medium text-gray-900">
                                                {business.first_name || business.last_name ? `${business.first_name || ''} ${business.last_name || ''}`.trim() : <span className="text-gray-400 italic">Unknown owner</span>}
                                            </div>
                                            <div className="text-sm text-gray-500">
                                                {business.user_email || <span className="text-gray-400 italic">No email</span>}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-center">
                                        {business.onboarding.connections.linked_stripe ? (
                                            <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                                        ) : (
                                            <XCircle className="h-5 w-5 text-gray-300 mx-auto" />
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-center">
                                        {business.onboarding.connections.linked_qbo ? (
                                            <div className="flex flex-col items-center gap-1">
                                                <CheckCircle className="h-5 w-5 text-green-500" />
                                                <div className="text-[11px] leading-tight text-gray-500">
                                                    {getQboSourceLabel(business)}
                                                </div>
                                                {(business.onboarding.qbo_metadata.connected_at || business.onboarding.connections.qbo_connected_at) && (
                                                    <div className="text-[11px] leading-tight text-gray-400">
                                                        {formatConnectedAt(business.onboarding.qbo_metadata.connected_at || business.onboarding.connections.qbo_connected_at)}
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <XCircle className="h-5 w-5 text-gray-300 mx-auto" />
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-center">
                                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${business.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'}`}>
                                            {business.status || 'active'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-center">
                                        <div className="flex justify-center gap-2">
                                            {/* <button
                                                onClick={() => qboSyncMutation.mutate(business.business_id || business.user_id)}
                                                disabled={!business.onboarding.connections.linked_qbo || qboSyncMutation.isPending}
                                                className="text-indigo-600 hover:text-indigo-900 disabled:text-gray-300 disabled:cursor-not-allowed"
                                                title={business.onboarding.connections.linked_qbo ? 'Sync QBO' : 'QBO not linked'}
                                            >
                                                <RefreshCw className={`h-5 w-5 ${qboSyncMutation.isPending && selectedBusinessForQboSync === (business.business_id || business.user_id) ? 'animate-spin' : ''}`} />
                                            </button>
                                            <button
                                                onClick={() => handleOpenRenormalize(business)}
                                                disabled={renormalizeMutation.isPending}
                                                className="text-indigo-600 hover:text-indigo-900 disabled:opacity-50"
                                                title="Renormalize Vendors"
                                            >
                                                <RefreshCcw className={`h-5 w-5 ${renormalizeMutation.isPending && selectedBusinessForRenormalize?.id === (business.business_id || business.user_id) ? 'animate-spin' : ''}`} />
                                            </button> */}
                                            <button
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    handleOpenExport(business);
                                                }}
                                                className="text-indigo-600 hover:text-indigo-900"
                                                title="Export Data"
                                            >
                                                <Download className="h-5 w-5" />
                                            </button>
                                            <button
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    setSelectedBusinessForBilling(business);
                                                }}
                                                disabled={!business.workspace_id}
                                                className="text-indigo-600 hover:text-indigo-900 disabled:text-gray-300 disabled:cursor-not-allowed"
                                                title={business.workspace_id ? 'Edit billing cards' : 'Missing workspace'}
                                            >
                                                <CreditCard className="h-5 w-5" />
                                            </button>
                                            <button
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    qbDesktopTokenMutation.mutate(business);
                                                }}
                                                disabled={!businessId || qbDesktopTokenMutation.isPending}
                                                className="text-indigo-600 hover:text-indigo-900 disabled:text-gray-300 disabled:cursor-not-allowed"
                                                title="Generate QuickBooks Desktop connector token"
                                            >
                                                <KeyRound className={`h-5 w-5 ${qbDesktopTokenMutation.isPending ? 'animate-pulse' : ''}`} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            )})}
                            {filteredBusinesses.length === 0 && (
                                <tr>
                                    <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                                        No businesses found matching your search.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {selectedBusinessForExport && (
                <DataExportModal
                    isOpen={exportModalOpen}
                    onClose={() => {
                        setExportModalOpen(false);
                        setSelectedBusinessForExport(null);
                    }}
                    businessId={selectedBusinessForExport.id}
                    businessName={selectedBusinessForExport.name}
                />
            )}

            {generatedConnectorToken && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
                    <div className="w-full max-w-2xl rounded-lg bg-white p-6 shadow-xl">
                        <h3 className="text-lg font-semibold text-gray-900">QuickBooks Desktop connector token</h3>
                        <p className="mt-1 text-sm text-gray-500">
                            Token for {generatedConnectorToken.businessName}. It is shown once; paste it into the Windows connector API token field.
                        </p>
                        <div className="mt-4 space-y-3">
                            <div>
                                <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">Business ID</div>
                                <div className="flex items-start gap-2">
                                    <div className="flex-1 rounded-md border border-gray-200 bg-gray-50 p-3 font-mono text-sm text-gray-900 break-all">
                                        {generatedConnectorToken.businessId}
                                    </div>
                                    <button
                                        onClick={() => navigator.clipboard?.writeText(generatedConnectorToken.businessId)}
                                        className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                                    >
                                        <Copy className="h-4 w-4" />
                                        Copy
                                    </button>
                                </div>
                            </div>
                            <div>
                                <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">API Token</div>
                                <div className="flex items-start gap-2">
                                    <div className="flex-1 rounded-md border border-gray-200 bg-gray-50 p-3 font-mono text-sm text-gray-900 break-all">
                                        {generatedConnectorToken.token}
                                    </div>
                                    <button
                                        onClick={() => navigator.clipboard?.writeText(generatedConnectorToken.token)}
                                        className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                                    >
                                        <Copy className="h-4 w-4" />
                                        Copy
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div className="mt-5 flex justify-end gap-2">
                            <button
                                onClick={() => setGeneratedConnectorToken(null)}
                                className="rounded-md bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800"
                            >
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {selectedBusinessForRenormalize && (
                <RenormalizeModal
                    isOpen={renormalizeModalOpen}
                    onClose={() => {
                        setRenormalizeModalOpen(false);
                        setSelectedBusinessForRenormalize(null);
                    }}
                    onConfirm={(params) => {
                        renormalizeMutation.mutate({
                            businessId: selectedBusinessForRenormalize.id,
                            params
                        });
                    }}
                    isPending={renormalizeMutation.isPending}
                    businessName={selectedBusinessForRenormalize.name || undefined}
                />
            )}

            {selectedBusinessForBilling?.workspace_id && (
                <AdminBillingPlansModal
                    isOpen={Boolean(selectedBusinessForBilling)}
                    workspaceId={selectedBusinessForBilling.workspace_id}
                    businessName={selectedBusinessForBilling.legal_name || selectedBusinessForBilling.dba_name || selectedBusinessForBilling.onboarding?.qbo_metadata?.company_name || selectedBusinessForBilling.user_email || selectedBusinessForBilling.business_id}
                    onClose={() => setSelectedBusinessForBilling(null)}
                />
            )}
        </div>
    );
};

export default AllBusinessesPage;
